import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, FieldAPI, FormAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import moment from 'moment';
import { FragmentNames, NursingInterventionsReverseAliases } from '../../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { Endpoint } from '../../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../../constants/routes';
import BaseController from '../../../../BaseController';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import MedicationService from '../../../services/medication-service';
import { ReasonType } from '../../adult-tpn/adult-tpn-types';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';
import { ChangeType } from '../nursing-interventions-types';

@Controller({ component: NursingInterventionsReverseAliases.NURSING_INTERVENTIONS_REVERSE_LAYOUT, fragment: FragmentNames.NURSING_INTERVENTIONS_REVERSE })
export default class NursingInterventionsReverseController extends BaseController {

  @Inject(NursingInterventionsReverseAliases.FORM_REASON)
  private formApi!: FormAPI;

  @Inject(NursingInterventionsReverseAliases.PANEL_LOADER)
  private feedbackPanelLoaderApi!: PanelAPI;

  @Inject(NursingInterventionsReverseAliases.AUTOCOMPLETE_REASON_LIST)
  private reasonAutocompleteApi!: AutocompleteAPI;

  @Inject(NursingInterventionsReverseAliases.NOTES_FIELD)
  private notesFieldApi!: FieldAPI;

  @OnMount(NursingInterventionsReverseAliases.NURSING_INTERVENTIONS_REVERSE_LAYOUT)
  handleMountLayout(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, patientId });
    InformationBarUtils.collapsedBar = true;
  }

  @OnBeforeCancel(NursingInterventionsReverseAliases.FORM_REASON)
  handleCancelForm(): void {
    applicationContext.popNavigationItem();
  }

  @OnBeforeSubmit(NursingInterventionsReverseAliases.FORM_REASON)
  handleSubmitForm(): void {
    this.reverse();
  }

  private reverse(): void {
    this.feedbackPanelLoaderApi.setVisible(true);
    const reasonSelected = this.reasonAutocompleteApi.getValue() as unknown as ReasonType;
    const alterationDate = moment().format(ISO_FULL_DATE_FORMAT);
    const eventDate = moment(MedicineAndSolutionsParams.params.startId).utc().format(ISO_FULL_DATE_FORMAT);

    const body: Record<string, Value> = {
      alterationDate,
      changeType: ChangeType.REVERT_ADMINISTRATION,
      encounterNumber: MedicineAndSolutionsParams.params.encounterId,
      eventDate,
      hourSequence: MedicineAndSolutionsParams.params.hourSequence,
      intervalCode: MedicineAndSolutionsParams.params.cdIntervalId,
      itemCode: MedicineAndSolutionsParams.params.cdMaterialId,
      itemSequence: MedicineAndSolutionsParams.params.nrSequenceId,
      itemType: MedicineAndSolutionsParams.params.ieTypeItem,
      justifyDescription: this.notesFieldApi.getValue() || '',
      prescriptionAdministrationNumber: MedicineAndSolutionsParams.params.nrPrescriptionId,
      prescriptionNumber: MedicineAndSolutionsParams.params.nrPrescriptionId,
      reasonSuspendSequence: reasonSelected.sequenceNumber,
      scheduleSequence: MedicineAndSolutionsParams.params.hourSequence,
      suspensionReasonSequence: reasonSelected.sequenceNumber,
    } as Record<string, Value>;

    HttpService.post(Endpoint.REVERSE_NURSING_INTERVENTIONS, {}, { body })
      .then(() => {
        MedicationService.showToast(MessageService.translate('text.revert-event'), ToastLevel.SUCCESS)
        this.goToTimeline()
      })
      .catch((error) => {
        const message = error.body.messageDescription;
        const errorMessage = error.body.errors.join('; ')
        MedicationService.showToast(message ?? errorMessage, ToastLevel.ERROR)
      }).finally(() => {
        this.formApi.setLocked(true);
      });
  }

  private goToTimeline(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
      encounterId,
      patientId,
    });
    applicationContext.clearNavigationStack();
  }
}